import React, { useState, useRef, useEffect, useCallback } from "react";
import { IconDots, IconClock } from "./Icons";
import "./AgentThreadsList.css";

export default function AgentThreadsList({
  threads, activeThreadId, onPick, onNewChat, onRename, onDelete,
  agentMap = {},
  newChatDisabled = false,
}) {
  // Kebab menu: { id, top, left } for the open thread, or null.
  const [menu, setMenu] = useState(null);
  // Inline rename: the thread id being edited + its working title.
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const menuRef = useRef(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  // Close the menu on outside click / Escape while it's open.
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    };
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu, closeMenu]);

  const openMenu = (e, threadId) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const width = 168;
    const left = Math.max(8, r.right - width);
    setMenu({ id: threadId, top: r.bottom + 4, left, width });
  };

  const startRename = (threadId) => {
    const t = threads.find((x) => x.thread_id === threadId);
    setEditValue(t?.title || "");
    setEditingId(threadId);
    closeMenu();
  };

  const commitRename = (threadId) => {
    const next = editValue.trim();
    setEditingId(null);
    const current = threads.find((x) => x.thread_id === threadId)?.title || "";
    if (next && next !== current) {
      onRename(threadId, next).catch((err) => alert(err.message));
    }
  };

  const doDelete = (threadId) => {
    closeMenu();
    const t = threads.find((x) => x.thread_id === threadId);
    if (window.confirm(`Delete chat "${t?.title || "Untitled"}"?`)) {
      onDelete(threadId).catch((err) => alert(err.message));
    }
  };

  return (
    <div className="agent-threads-list">
      <div className="atl-header">
        <button
          type="button"
          className="atl-new-link"
          onClick={onNewChat}
          disabled={newChatDisabled}
          title={newChatDisabled ? "Select an agent first" : "Start a new chat"}
        >+ New chat</button>
      </div>
      <div className="atl-body">
        {threads.length === 0 && <div className="atl-empty">No chats yet.</div>}
        {threads.map((t) => {
          const agent = agentMap[t.agent_id];
          const running = t.status === "running";
          const terminated = t.status === "terminated";
          const editing = editingId === t.thread_id;
          return (
            <div
              key={t.thread_id}
              className={`atl-row-wrap ${activeThreadId === t.thread_id ? "active" : ""} ${running ? "is-running" : ""} ${editing ? "editing" : ""}`}
            >
              {editing ? (
                <div className="atl-row atl-row--editing">
                  <span className={`atl-status atl-status--${t.status}`} />
                  <input
                    className="atl-title-input"
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitRename(t.thread_id); }
                      else if (e.key === "Escape") { e.preventDefault(); setEditingId(null); }
                    }}
                    onBlur={() => setEditingId(null)}
                    aria-label="Chat name"
                  />
                </div>
              ) : (
                <button
                  className="atl-row"
                  onClick={() => onPick(t.thread_id)}
                  title={running ? "Agent is responding…" : (t.title || "Untitled")}
                >
                  <span
                    className={`atl-status atl-status--${t.status}`}
                    aria-label={running ? "Agent is responding" : terminated ? "Stopped" : undefined}
                  />
                  <span className="atl-title">
                    {t.is_scheduled && (
                      <span className="atl-clock" title="Created by a scheduled task">
                        <IconClock size={12} />
                      </span>
                    )}
                    {t.title || "Untitled"}
                  </span>
                  <span className="atl-sub">
                    {running ? (
                      <span className="atl-responding">Responding…</span>
                    ) : (
                      <>
                        {agent && <span className="atl-agent">{agent.name}</span>}
                        {t.last_message_preview && (
                          <span className="atl-preview">{t.last_message_preview}</span>
                        )}
                      </>
                    )}
                  </span>
                </button>
              )}
              {!editing && (
                <div className="atl-actions">
                  <button
                    className={`atl-kebab ${menu?.id === t.thread_id ? "open" : ""}`}
                    title="More"
                    aria-label="Chat options"
                    aria-haspopup="menu"
                    onClick={(e) => openMenu(e, t.thread_id)}
                  ><IconDots size={16} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {menu && (
        <div
          ref={menuRef}
          className="atl-menu"
          role="menu"
          style={{ top: menu.top, left: menu.left, width: menu.width }}
        >
          <button className="atl-menu-item" role="menuitem" onClick={() => startRename(menu.id)}>
            Rename
          </button>
          <button
            className="atl-menu-item atl-menu-item--danger"
            role="menuitem"
            onClick={() => doDelete(menu.id)}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
